import {
  GrayColorScheme,
  getBranchColorScheme,
} from '@flowx/designer/shared/versioning/versioning.utils'
import { TemplateName, createG, createRect, createText, templateExtend } from '@flowx/gitgraph'
import { svgElementFromString } from '@flowx/shared/utils'

import { ApplicationStatus, ApplicationVersion } from '../../entities/application.entity'

enum DefaultBranchName {
  MAIN = 'main',
}

export enum GitEvents {
  COMMIT = 'commit',
  BRANCH = 'branch',
  MERGE = 'merge',
}

const GitGraphColors = {
  fontColor: '#0F1114',
  mainLight: '#E6F0FB',
  featureLight: '#FFF8E7',
  mainAccent: '#006BD8',
  featureAccent: '#FEB913',
  mainWhite: '#FFFFFF',
  // Blue-100 ring around the selected bubble (Branching & versioning §3)
  selectionHalo: '#B0D1F3',
}

const BranchStyle = {
  lineWidth: 2,
  label: {
    borderRadius: 4,
  },
  borderRadius: 4,
  spacing: 30,
}
const CommitStyle = {
  spacing: 36,
  message: {
    display: false,
  },
}

export const CustomTemplate = templateExtend(TemplateName.Metro, {
  branch: BranchStyle,
  commit: CommitStyle,
})

export function branchStyle(log: ApplicationVersion, highlighted: boolean): any {
  const { branchName } = log
  // Missing (no content) branches render their lines in the reserved gray, never a branch hue
  const scheme = log.hasContent ? getBranchColorScheme(log.branchOrder) : GrayColorScheme
  return {
    color: highlighted ? scheme.primary : scheme.secondary,
    label: {
      bgColor:
        branchName === DefaultBranchName.MAIN
          ? GitGraphColors.mainLight
          : GitGraphColors.featureLight,
      color: GitGraphColors.fontColor,
      strokeColor:
        branchName === DefaultBranchName.MAIN
          ? GitGraphColors.mainAccent
          : GitGraphColors.featureAccent,
      borderRadius: 4,
      font: '10pt Open Sans',
    },
  }
}

export function renderBranchLabel(branch: any): any {
  const LABEL_PADDING_X = 4
  const LABEL_PADDING_Y = 2

  const rect = createRect({
    width: 0,
    height: 0,
    borderRadius: branch.style.label.borderRadius,
    stroke: branch.style.label.strokeColor,
    fill: branch.style.label.bgColor,
  })
  const text = createText({
    content: branch.name,
    translate: {
      x: LABEL_PADDING_X,
      y: 0,
    },
    font: branch.style.label.font,
    fill: branch.style.label.color,
  })

  const branchLabel = createG({ children: [rect] })

  const observer = new MutationObserver(() => {
    const { height, width } = text.getBBox()

    const boxWidth = width + 2 * LABEL_PADDING_X
    const boxHeight = height + 2 * LABEL_PADDING_Y

    // Ideally, it would be great to refactor these behavior into SVG elements.
    rect.setAttribute('width', boxWidth.toString())
    rect.setAttribute('height', boxHeight.toString())
    text.setAttribute('y', (boxHeight / 2).toString())
  })

  observer.observe(branchLabel, {
    attributes: false,
    subtree: false,
    childList: true,
  })

  // Add text after observer is set up => react based on text size.
  // We might refactor it by including `onChildrenUpdate()` to `createG()`.
  branchLabel.appendChild(text)

  return branchLabel
}

export function renderDot(
  commitLog: ApplicationVersion,
  isCurrent: boolean,
  highlighted: boolean,
  commit: any
): any {
  const dot = createG({
    children: [svgElementFromString(getSvgByCommitState(commitLog, isCurrent, highlighted))],
    onClick: () => {
      commit.onClick()
    },
  })
  dot.setAttribute('data-commit-id', commitLog.id)
  return dot
}

// Phosphor bold glyph outlines extracted from the bundled icon font
// (font coordinates: 1024 units/em, ascent 960, y-axis up — flipped by glyphTransform)
const ARROWS_MERGE_GLYPH =
  'M784 800v-256c0-0.011 0-0.024 0-0.038 0-13.245-5.365-25.238-14.040-33.923l-209.96-210.040v-152l46.040 46.080c8.701 8.701 20.722 14.083 34 14.083 26.556 0 48.083-21.528 48.083-48.083 0-13.278-5.382-25.299-14.083-34l-128-128c-8.692-8.721-20.716-14.118-34-14.118s-25.308 5.396-33.999 14.116l-128.001 128.001c-8.701 8.701-14.083 20.722-14.083 34 0 26.556 21.528 48.083 48.083 48.083 13.278 0 25.299-5.382 34-14.083v0l45.96-46.080v152l-209.96 210.040c-8.675 8.684-14.040 20.677-14.040 33.922 0 0.013 0 0.027 0 0.040v-0.002 256c0 26.51 21.49 48 48 48s48-21.49 48-48v0-236l176-176 176 176v236c0 26.51 21.49 48 48 48s48-21.49 48-48v0z'
const MINUS_GLYPH =
  'M912 448c0-26.51-21.49-48-48-48v0h-704c-26.51 0-48 21.49-48 48s21.49 48 48 48v0h704c26.51 0 48-21.49 48-48v0z'

const BUBBLE_CANVAS = 26
const BUBBLE_CENTER = BUBBLE_CANVAS / 2
const HALO_RADIUS = 12
const BUBBLE_RADIUS = 8
const STROKE_WIDTH = 2
const ICON_SIZE = 10
// Dash gap derived from the circumference so an integer number of periods closes seamlessly
const DASH_LENGTH = 3
const DASH_GAP = ((2 * Math.PI * BUBBLE_RADIUS) / 9 - DASH_LENGTH).toFixed(2)
const FONT_UNITS_PER_EM = 1024
const FONT_ASCENT = 960

function glyphTransform(iconSizePx: number): string {
  const scale = iconSizePx / FONT_UNITS_PER_EM
  const offset = (BUBBLE_CANVAS - iconSizePx) / 2
  return `translate(${offset} ${offset + FONT_ASCENT * scale}) scale(${scale} -${scale})`
}

type BubbleOptions = {
  fill: string
  stroke: string
  dashed?: boolean
  halo?: boolean
  icon?: { glyph: string; color: string; sizePx: number }
}

// Design-spec §3 (Branching & versioning — version bubbles): every bubble is a 16px circle
// (r=8, 2px border) centered on a 26px canvas; the selected bubble additionally sits on a
// 24px halo disc (r=12, white fill, blue-100 ring). Draft states draw the border dashed.
function bubbleSvg({ fill, stroke, dashed, halo, icon }: BubbleOptions): string {
  // The halo circle is always rendered (transparent when unselected) so every dot occupies the
  // same geometry — dropping it resizes the svg and makes the graph jitter on selection
  const haloCircle = `<circle cx="${BUBBLE_CENTER}" cy="${BUBBLE_CENTER}" r="${HALO_RADIUS}" fill="${halo ? GitGraphColors.mainWhite : 'transparent'}" stroke="${halo ? GitGraphColors.selectionHalo : 'transparent'}" stroke-width="${STROKE_WIDTH}"/>`
  const dashPattern = dashed ? ` stroke-dasharray="${DASH_LENGTH} ${DASH_GAP}"` : ''
  const bubbleCircle = `<circle cx="${BUBBLE_CENTER}" cy="${BUBBLE_CENTER}" r="${BUBBLE_RADIUS}" fill="${fill}" stroke="${stroke}" stroke-width="${STROKE_WIDTH}"${dashPattern}/>`
  const iconPath = icon
    ? `<path d="${icon.glyph}" fill="${icon.color}" transform="${glyphTransform(icon.sizePx)}"/>`
    : ''
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${BUBBLE_CANVAS}" height="${BUBBLE_CANVAS}" viewBox="0 0 ${BUBBLE_CANVAS} ${BUBBLE_CANVAS}">
            ${haloCircle}
            ${bubbleCircle}
            ${iconPath}
          </svg>`
}

// Minus for versions that aren't usable on this env (missing content or archived branch),
// merge arrows for merge results, nothing otherwise
function commitIcon(commit: ApplicationVersion, color: string): BubbleOptions['icon'] | undefined {
  const glyph =
    commit.status !== ApplicationStatus.WIP && (!commit.hasContent || commit.branchArchived)
      ? MINUS_GLYPH
      : commit.mergedFromVersionId ||
          commit.mergedInAnyBranch ||
          commit.status === ApplicationStatus.MERGE_IN_PROGRESS
        ? ARROWS_MERGE_GLYPH
        : null
  return glyph ? { glyph, color, sizePx: ICON_SIZE } : undefined
}

function getSvgByCommitState(
  commit: ApplicationVersion,
  isCurrent: boolean,
  highlighted: boolean
): string {
  const scheme =
    commit.hasContent && !commit.branchArchived
      ? getBranchColorScheme(commit.branchOrder)
      : GrayColorScheme
  const branchColor = highlighted ? scheme.primary : scheme.secondary

  // Selected — branch-color fill (white + dashed while WIP), branch-color border, blue-100 halo;
  // overrides status styling
  if (isCurrent) {
    const isWip = commit.status === ApplicationStatus.WIP
    const isMergeInProgress = commit.status === ApplicationStatus.MERGE_IN_PROGRESS
    return bubbleSvg({
      fill: isWip || isMergeInProgress ? GitGraphColors.mainWhite : branchColor,
      stroke: branchColor,
      dashed: isWip || isMergeInProgress,
      halo: true,
      icon: commitIcon(commit, isMergeInProgress ? branchColor : GitGraphColors.mainWhite),
    })
  }

  // Missing (not on this env) or archived — hollow white fill, gray border + gray minus icon
  if (!commit.hasContent || commit.branchArchived) {
    const grayColor = highlighted ? GrayColorScheme.primary : GrayColorScheme.secondary
    return bubbleSvg({
      fill: GitGraphColors.mainWhite,
      stroke: grayColor,
      icon: commitIcon(commit, grayColor),
    })
  }

  switch (commit.status) {
    // Merge conflict (Merge draft) — draft bubble + branch-color merge icon
    case ApplicationStatus.MERGE_IN_PROGRESS: {
      return bubbleSvg({
        fill: GitGraphColors.mainWhite,
        stroke: branchColor,
        dashed: true,
        icon: commitIcon(commit, branchColor),
      })
    }

    // Draft — hollow white fill, dashed border in branch color
    case ApplicationStatus.WIP: {
      return bubbleSvg({ fill: GitGraphColors.mainWhite, stroke: branchColor, dashed: true })
    }

    // Committed — solid branch color; merged versions add the white merge icon
    default: {
      return bubbleSvg({
        fill: branchColor,
        stroke: branchColor,
        icon: commitIcon(commit, GitGraphColors.mainWhite),
      })
    }
  }
}

type GitEvent = { eventType: GitEvents; log: ApplicationVersion; parentBranchName?: string }

interface BranchTrackingMaps {
  branchEventsToInsert: Map<ApplicationVersion['branchName'], GitEvent>
  branchesCreatedFrom: Map<ApplicationVersion['id'], ApplicationVersion['branchName'][]>
}

function collectBranchEventsAndDependencies(gitEvents: GitEvent[]): BranchTrackingMaps {
  const branchEventsToInsert: Map<ApplicationVersion['branchName'], GitEvent> = new Map()
  const branchesCreatedFrom: Map<ApplicationVersion['id'], ApplicationVersion['branchName'][]> =
    new Map()

  gitEvents.forEach((event) => {
    if (event.eventType === GitEvents.BRANCH) {
      // Store all branch events
      branchEventsToInsert.set(event.log.branchName, event)

      // For branches created from a specific commit, track by previousVersionId
      if (event.parentBranchName && event.log.previousVersionId) {
        const branches = branchesCreatedFrom.get(event.log.previousVersionId) || []
        branches.push(event.log.branchName)
        branchesCreatedFrom.set(event.log.previousVersionId, branches)
      }
    }
  })

  return { branchEventsToInsert, branchesCreatedFrom }
}

function insertBranchIfFirstSeen(
  branchName: ApplicationVersion['branchName'],
  branchFirstSeen: Set<ApplicationVersion['branchName']>,
  branchEventsToInsert: Map<ApplicationVersion['branchName'], GitEvent>,
  reorderedEvents: GitEvent[]
): void {
  if (!branchFirstSeen.has(branchName)) {
    branchFirstSeen.add(branchName)
    const branchEvent = branchEventsToInsert.get(branchName)
    if (branchEvent && !branchEvent.log.previousVersionId) {
      // insert event only if it has no previousVersionId
      reorderedEvents.push(branchEvent)
      branchEventsToInsert.delete(branchName)
    }
  }
}

function insertBranchesCreatedFromCommit(
  commitId: ApplicationVersion['id'],
  branchesCreatedFrom: Map<ApplicationVersion['id'], ApplicationVersion['branchName'][]>,
  branchFirstSeen: Set<ApplicationVersion['branchName']>,
  branchEventsToInsert: Map<ApplicationVersion['branchName'], GitEvent>,
  reorderedEvents: GitEvent[]
): void {
  const branchesToCreate = branchesCreatedFrom.get(commitId) || []
  branchesToCreate.forEach((branchName) => {
    const branchEvent = branchEventsToInsert.get(branchName)
    if (branchEvent && !branchFirstSeen.has(branchName)) {
      branchFirstSeen.add(branchName)
      reorderedEvents.push(branchEvent)
      branchEventsToInsert.delete(branchName)
    }
  })
}

function insertRemainingBranchEvents(
  branchEventsToInsert: Map<ApplicationVersion['branchName'], GitEvent>,
  reorderedEvents: GitEvent[]
): void {
  // Add any remaining branch events (shouldn't happen with correct data)
  // this is where branches with previousVersionId are inserted
  branchEventsToInsert.forEach((event) => {
    const index = reorderedEvents.findIndex((e) => e.log.id === event.log.previousVersionId)
    if (index !== -1) {
      reorderedEvents.splice(index + 1, 0, event)
      return
    }
    reorderedEvents.push(event)
  })
}

function reorderGitEventsAsGraph(gitEvents: GitEvent[]): GitEvent[] {
  const reorderedEvents: GitEvent[] = []
  const branchFirstSeen: Set<ApplicationVersion['branchName']> = new Set()

  // Collect all branch events and track dependencies
  const { branchEventsToInsert, branchesCreatedFrom } =
    collectBranchEventsAndDependencies(gitEvents)

  // Process events in order
  gitEvents.forEach((event) => {
    if (event.eventType === GitEvents.BRANCH) {
      // Skip branch events in first pass - they'll be inserted at the right time
      return
    }

    // For commits and merges, check if we need to insert a branch event first
    insertBranchIfFirstSeen(
      event.log.branchName,
      branchFirstSeen,
      branchEventsToInsert,
      reorderedEvents
    )

    // Add the current event
    reorderedEvents.push(event)

    // After each commit, check if any branches should be created from it
    if (event.eventType === GitEvents.COMMIT) {
      insertBranchesCreatedFromCommit(
        event.log.id,
        branchesCreatedFrom,
        branchFirstSeen,
        branchEventsToInsert,
        reorderedEvents
      )
    }
  })

  // Add any remaining branch events
  insertRemainingBranchEvents(branchEventsToInsert, reorderedEvents)

  return reorderedEvents
}

export function createGitEventLog(commits: ApplicationVersion[]): GitEvent[] {
  const gitEvents: GitEvent[] = []
  const gitLogById = commits.reduce(
    (acc, log) => ({
      ...acc,
      [log.id]: log,
    }),
    {}
  )

  // Track branches that have been created to avoid duplicates
  const createdBranches = new Set<ApplicationVersion['branchName']>()

  // First pass: add all commits and merges in order
  commits.forEach((log) => {
    const previousBranch: ApplicationVersion = gitLogById[log.previousVersionId]

    if (!log.previousVersionId) {
      createdBranches.add(log.branchName)
      gitEvents.push({ eventType: GitEvents.BRANCH, log }, { eventType: GitEvents.COMMIT, log })
    } else if (log.mergedFromVersionId) {
      gitEvents.push({ eventType: GitEvents.MERGE, log })
    } else {
      // For regular commits, check if we need to create a branch
      if (log.branchName !== previousBranch.branchName && !createdBranches.has(log.branchName)) {
        createdBranches.add(log.branchName)
        // Pass the parent branch name for proper branch creation
        gitEvents.push(
          {
            eventType: GitEvents.BRANCH,
            log,
            parentBranchName: previousBranch.branchName,
          },
          { eventType: GitEvents.COMMIT, log }
        )
      } else {
        gitEvents.push({ eventType: GitEvents.COMMIT, log })
      }
    }
  })

  // Second pass: reorder to put branch events right after their parent commit
  return reorderGitEventsAsGraph(gitEvents)
}
